import * as angular from 'angular';
import * as _ from 'lodash';

import { IApplicationConfiguration, IFrequency, TagManualInputValues } from '../../../models';
import { manualInputDateFormats, toDictionary } from '../../../common';

import { ManualInputsWidget } from '../../../widgets/manualInputs/manualInputs.widget';
import { ManualInputWidgetProperties } from '../../../widgets/manualInputs/extraProperties';
import { IManualInputsDataService } from '../../../widgets/manualInputs/manualInputsData.service';

declare type ManualInputsWidgetWithExtraProperties = ManualInputsWidget & ManualInputWidgetProperties;

export interface IManualInputsWidgetsService {
    getWidgetRange(
        widget: { configuration: { maxColumns: number } },
        freq: IFrequency,
        application: IApplicationConfiguration,
        beginDate: Date,
        endDate: Date
    ): Date[];
    readManualInputs(
        widgets: ManualInputsWidgetWithExtraProperties[],
        application: IApplicationConfiguration,
        begin: Date,
        end: Date,
        hasApproval: boolean,
        force?: boolean
    ): angular.IPromise<ManualInputsWidgetWithExtraProperties[]>;
}

export class ManualInputsWidgetsService implements IManualInputsWidgetsService {
    static serviceId = 'manualInputsWidgetsService';

    static $inject = ['$q', 'manualInputsDataService'];

    constructor(private _$q: angular.IQService, private _manualInputsDataService: IManualInputsDataService) {}

    getWidgetRange(
        widget: { configuration: { maxColumns: number } },
        freq: IFrequency,
        application: IApplicationConfiguration,
        beginDate: Date,
        endDate: Date
    ): Date[] {
        const result = this._manualInputsDataService.getRangeSync(beginDate, endDate, freq, application, widget.configuration.maxColumns || 31, true);
        return result.dates;
    }

    readManualInputs(
        widgets: ManualInputsWidgetWithExtraProperties[],
        application: IApplicationConfiguration,
        beginDate: Date,
        endDate: Date,
        hasApproval: boolean,
        force: boolean = false
    ): angular.IPromise<ManualInputsWidgetWithExtraProperties[]> {
        const appFrequency = {
            interval: application.interval,
            intervalTimeUnit: application.intervalTimeUnit
        };
        const promises = widgets.map((w) => {
            // #27382 : inputList is empty...
            const freq = w.inputList && w.inputList.length ? w.inputList[0].frequency : appFrequency;
            w.dateFormat = w.configuration.dateFormat || manualInputDateFormats[freq.intervalTimeUnit];
            return this._manualInputsDataService.getRange(beginDate, endDate, freq, w.configuration.maxColumns || 31);
        });
        return this._$q
            .all(promises)
            .then((data) => {
                widgets.forEach((w, i) => {
                    w.dates = data[i].dates;
                    w.isExceeded = data[i].isExceeded;
                    w.inputList = w.inputList
                        ? w.inputList.map((input) => {
                              const copy = angular.copy(input);
                              copy.values = w.dates.map((date) => {
                                  return { date };
                              });
                              return copy;
                          })
                        : [];
                });
                const il = [].concat(...widgets.map((w) => w.inputList || ([] as TagManualInputValues[]))) as TagManualInputValues[];
                const dates = _.uniq([].concat(...data.map((d) => d.dates)));
                return this.readManualInputData(widgets, dates, il, hasApproval, force);
            })
            .catch((reason) => {
                console.error(reason);
                throw reason;
            });
    }

    private readManualInputData(
        widgets: ManualInputsWidgetWithExtraProperties[],
        dates: Date[],
        il: TagManualInputValues[],
        hasApproval: boolean,
        force: boolean = false
    ) {
        return this._manualInputsDataService.loadData(dates, il, hasApproval, force).then((data) => {
            widgets.forEach((w) => {
                const index = toDictionary(data);
                w.inputList = w.inputList.map((i, rowIndex) => {
                    const copy = angular.copy(i);
                    const indexed = index[i.guid];
                    (indexed?.values ?? []).forEach((v, colIndex) => {
                        if (v.timestampOptions) {
                            v.timestampOptions.options.eventHandlers['apply.daterangepicker'] = (evt, _picker) => {
                                try {
                                    v.newValue = evt.model.unix();
                                    const x = w.configuration.isVertical ? colIndex : rowIndex;
                                    const y = w.configuration.isVertical ? rowIndex : colIndex;
                                    w.ctrl.form[`value_${x}_${y}`].$setDirty();
                                } catch (e) {
                                    console.error(e);
                                }
                            };
                        }
                    });
                    copy.values = indexed.values;
                    return copy;
                });
                if (w.configuration.showLogbook && w.inputList) {
                    w.inputList = this._manualInputsDataService.mergeInputListWithEntries(
                        w.inputList,
                        (w as any).data?.entries || [],
                        w.inputList[0].frequency
                    );
                }
                w._pristine = angular.copy(w.inputList);
            });
            return widgets;
        });
    }
}
